<template>
  <div class="ov-x-hidden" selenium="pro-account-occupation">
    <auth-redirect-form
        ref="form"
        :source="source"
        :occup-id="occupId"/>
    <h5 class="HEADING-LAYOUT">
      Occupations
    </h5>
    <occupation-tabs />

    <transition name="fade" mode="out-in">
      <div v-if="loading" class="d-flex jc-center">
        <spinner size="100px"/>
      </div>

      <div v-else-if="serviceProviderOccupationId" :key="serviceProviderOccupationId">
        <template v-if="isOccupationSetupInProgress">
          <h1
              class="text-hero text-capitalize m-y-0 m-b-5 m-t-0"
              selenium="public-profile-occupation">
            {{ occupationName }}
          </h1>
          <div class="d-flex ai-center fd-column">
            <div class="h3 m-b-3">
              To continue onboarding this application click below.
            </div>
            <button
                class="btn"
                @click="submitRedirect({source: 'expansion', occupId: occupationId})">
              Continue
            </button>
          </div>
        </template>
        <template v-if="isOccupationInWaitlisted">
          <h1
              class="text-hero text-capitalize m-y-0 m-b-5 m-t-0"
              selenium="public-profile-occupation">
            {{ occupationName }}
          </h1>
          <div class="d-flex ai-center fd-column">
            <div class="h6 m-b-3">
              You are currently waitlisted. We'll notify you when there are openings.
            </div>
          </div>
        </template>
        <template v-if="isOccupationActive">
          <div class="layout ai-center:t">
            <div class="layout-item is-12 is-9:t">
              <h1
                  class="text-hero text-capitalize text-center:mo m-b-3:mo m-y-0 p-r-3:t"
                  selenium="public-profile-occupation"
              >
                {{ occupationName }}
              </h1>
            </div>
            <div class="layout-item text-center text-right:t is-12 is-3:t">
              <a
                  class="btn-tertiary btn-sm d-flex flex-center m-b-2"
                  selenium="pro-account-occupation-link-add"
                  @click="submitRedirect({source: 'expansion', editType: 'Add New Occupation'})">
                Add New Occupation
              </a>
              <router-link
                  :to="{
                    name: 'pro-public-profile',
                    params: { serviceProviderOccupationId }
                  }"
                  class="btn-secondary btn-sm d-flex flex-center"
                  selenium="pro-account-occupation-link-public">
                View Public Profile
              </router-link>
            </div>
          </div>

          <div class="DIVIDER"/>

          <h5 class="HEADING d-flex fd-column:mo">
            Services Offered
            <span class="cur-pointer">
              <img class="c-depot m-l-1" src="./assets/Edit.svg" />
              <a
                  class="c-depot h6 UNDER"
                  selenium="pro-account-occupation-link-editservices"
                  @click="submitRedirect({
                    source: 'services',
                    occupId: occupationId,
                    editType: 'Services Offered'})">
                Edit Services
              </a>
            </span>
            <span v-if="showPricing" class="cur-pointer">
              <img class="c-depot m-l-1" src="./assets/Edit.svg" />
              <a
                  @click="toggleEditPricing"
                  class="c-depot h6 UNDER">
                Edit Prices
              </a>
            </span>
          </h5>
          <div  v-if="showPricing" class="d-flex">
            <vk-icon
                name="information-filled"
                size="18"
                class="p-l-2 p-r-1 flex-0-0"
                unbounded/>
            <span>
              Prices should estimate the cost of your labor for the service.
              You do not need to include an estimate of the material cost in your pricing.
            </span>
          </div>
          <services-pricing v-if="showPricing" :services="orderedServices"/>
          <div
              v-else
              class="layout gap-2 fd-column:mo"
              selenium="pro-account-occupation-section-services">
            <div
                v-for="service in servicesToDisplay"
                :key="service.id"
                class="layout-item d-flex width-100 is-4:t is-3:d"
                selenium="pro-account-occupation-service">
              <vk-icon name="checkmark" class="flex-0-0 text-sm c-green m-t-1 m-r-1"/>
              <p
                class="ov-hidden">{{ service.name }}</p>
            </div>
          </div>
          <button
              v-if="services.length > servicesShown"
              :class="{ 'd-none': showPricing }"
              class="btn-secondary btn-sm text-capitalize m-t-3"
              selenium="pro-account-occupation-button-showservices"
              @click="showAllServices = !showAllServices">
            Show {{ showAllServices ? 'less' : 'more' }}
          </button>

          <div class="DIVIDER"/>

          <h5 class="HEADING" selenium="public-profile-work-area">
            Work Area
            <a
                class="link h6 m-l-1 c-depot"
                selenium="pro-account-occupation-link-editworkarea"
                @click="submitRedirect({
                  source: 'workarea',
                  occupId: occupationId,
                  editType: 'Work Area'}
            )">
              Edit
            </a>
          </h5>
          <div selenium="pro-account-occupation-section-workarea">
            <work-area v-if="locations.length" :locations="locations"/>
          </div>

          <div class="DIVIDER"/>

          <h5 class="HEADING">
            License Documents
          </h5>
          <div selenium="pro-account-occupation-section-licenses">
            <div class="layout h6 text-bold gap-3">
              <div class="layout-item is-4">License</div>
              <div class="layout-item is-4">Expiration Date</div>
              <div class="layout-item is-4">Status</div>
            </div>
            <div v-if="licenses.length">
              <div v-for="(license, index) in licenses" :key="index" class="layout text-capitalize gap-3">
                <div class="layout-item ellipsize is-4">
                  <div>
                    {{ license.document_meta_name }}
                  </div>
                  <div>
                    {{ license.document_identifier }}
                  </div>
                </div>
                <div class="layout-item is-4">{{ license.end_date | formatDate('M/d/yyyy') }}</div>
                <div class="layout-item ellipsize is-4">{{ license.document_status }}</div>
              </div>
            </div>
          </div>

          <div class="DIVIDER"/>

          <h5 class="HEADING">
            Photos
            <label  @click="onAddPhotoClick"   :class="{ 'cur-not-allowed': uploadInProgress }" class="link h6 m-l-1 c-depot">
              Add
            </label>
            <input
                ref="galleryInput"
                :disabled="uploadInProgress"
                class="d-none"
                multiple
                type="file"
                accept="image/*"
                @change="uploadPhotos"
            />
            <input
                ref="cameraInput"
                :disabled="uploadInProgress"
                class="d-none"
                capture="environment"
                multiple
                type="file"
                accept="image/*"
                @change="uploadPhotos"
            />
            <spinner v-if="uploadInProgress" class="d-iblock m-l-1" size="16px" color="depot"/>
          </h5>
          <div selenium="pro-account-occupation-section-photos">
            <gallery v-if="photos.length" :files="photos" deletable @deleteImage="deletePhoto"/>
            <div v-else class="text-center m-t-6">
              No photos were found for this occupation.
            </div>
            <photo-info class="m-l-1"/>
            <special-message ref="uploadPhotoErrorMessage" type="error" class="PHOTO-UPLOAD-ERROR">
              {{ uploadPhotoError || 'Failed to upload some photos. Please try again later.' }}
            </special-message>
          </div>

          <div class="DIVIDER"/>

          <h5 class="HEADING">
            Delete This Occupation
          </h5>
          <div selenium="pro-account-occupation-section-delete">
            If you don’t want to provide this service anymore and want
            to delete this occupation from your profile please call
            <a href="tel:18557232266" class="link">1-855-723-2266</a>
          </div>
        </template>
      </div>
    </transition>
    <image-popup
        :isOpen="isShowImagePopup"
        @close= "isShowImagePopup=false"/>

    <pricing-modal
        v-if="openPricing"
        @close="toggleEditPricing"
        :services="orderedServices"
        :occupation="occupationName"
        :occupation-id="serviceProviderOccupationId"
        :loading="loading"
    />
    <vk-modal class="gallery-popup-overlay" v-if="showGalleryPopup" @close="closeGalleryPopup">
      <div class="gallery-popup">
        <div class="popup-content">
          <h3>Select Images</h3>
          <button
              class="BUTTON"
              @click="() => { this.$refs.galleryInput.click(); closeGalleryPopup(); }"
          >
            Choose from Gallery
          </button>
          <button class="BUTTON" @click="openCamera">Capture from Camera</button>
          <button class="BUTTONCLOSE" @click="closeGalleryPopup">Close</button>
        </div>
      </div>
    </vk-modal>
  </div>
</template>

<script>
import _uniq from 'lodash/uniq';
import { mapGetters, mapActions } from 'vuex';
import { getCoverAreasDetails } from '@homeservices/api-kit/serviceprovider/coverareas';
import { getPublicProfiles } from '@homeservices/api-kit/search/publicprofile';
import {
  getOccupationsCoverAreas,
  getOccupationsAssets,
  getOccupationsDocuments,
} from '@homeservices/api-kit/serviceprovider/occupations';
import { geocode } from '@homeservices/api-kit/search';

import AuthRedirectForm from '@/components/AuthRedirectForm';
import SpecialMessage from '@/components/SpecialMessage';
import WorkArea from '@/components/WorkArea';
import { uploadFiles, deleteFile } from '@/utils/file';

import PhotoInfo from '@/components/PhotoInfo';
import ServicesPricing from '@/components/ServicesPricing';
import PricingModal from '@/components/ServicesPricing/PricingModal';
import ImagePopup from '@/components/ServicesPricing/ImagePopup';
import OccupationTabs from './components/OccupationTabs';

const RESULTS_SIZE = 100;

export default {
  name: 'account-occupation',
  components: {
    AuthRedirectForm,
    SpecialMessage,
    WorkArea,
    OccupationTabs,
    PhotoInfo,
    ServicesPricing,
    PricingModal,
    ImagePopup,
  },
  props: {
    serviceProviderOccupationId: [String, Number],
  },
  data () {
    return {
      services: [],
      licenses: [],
      locations: [],
      photos: [],
      uploadPhotoError: '',
      uploadInProgress: false,
      showAllServices: false,
      servicesShown: 10,
      source: '',
      occupId: '',
      openPricing: false,
      loading: true,
      isShowImagePopup: false,
      showGalleryPopup: false,
    };
  },
  computed: {
    ...mapGetters('account/pro', [
      'serviceProviderCatalogOccupations',
    ]),
    ...mapGetters('account/pro/pricing', [
      'pricingList',
    ]),
    isAndroid () {
      return /Android/i.test(navigator.userAgent);
    },
    serviceProviderOccupation () {
      return this.serviceProviderCatalogOccupations[this.serviceProviderOccupationId]
        || { occupation: {} };
    },
    isOccupationSetupInProgress () {
      return this.serviceProviderOccupation.service_provider_occupation_status === 'expansion'
        || this.serviceProviderOccupation.service_provider_occupation_status === 'draft';
    },
    isOccupationInWaitlisted () {
      return this.serviceProviderOccupation.service_provider_occupation_status === 'waitlisted';
    },
    isOccupationActive () {
      return !(this.isOccupationSetupInProgress || this.isOccupationInWaitlisted);
    },
    serviceProviderId () {
      return this.serviceProviderOccupation.service_provider_id;
    },
    occupationId () {
      return this.serviceProviderOccupation.occupation_id;
    },
    occupationName () {
      return this.serviceProviderOccupation.occupation.display_name;
    },
    servicesToDisplay () {
      const endIndex = this.showAllServices ? this.services.length : this.servicesShown;
      return this.services.slice(0, endIndex);
    },
    showPricing () {
      return this.pricingList[this.occupationId] && this.pricingList[this.occupationId].pricing;
    },
    orderedServices () {
      const servicesCopy = this.services.slice();
      return servicesCopy.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {
    serviceProviderCatalogOccupations () {
      this.setServiceProviderOccupationId();
    },
    serviceProviderOccupationId () {
      this.setServiceProviderOccupationId();
      this.getPhotos();
      this.getLicenseDocuments();
    },
    occupationId () {
      this.getServices();
    },
    serviceProviderId () {
      this.getWorkArea();
    },
  },
  async mounted () {
    if (Object.keys(this.pricingList).length === 0) {
      await this.fetchPricingList();
    }

    await this.setServiceProviderOccupationId();

    if (this.serviceProviderOccupationId) {
      await this.getPhotos();
      await this.getLicenseDocuments();
    }

    if (this.occupationId) {
      await this.getServices();
    }

    if (this.serviceProviderId) {
      await this.getWorkArea();
    }
    this.loading = false;
  },
  methods: {
    onAddPhotoClick () {
      if (this.isAndroid) {
        this.showGalleryPopup = true;
      } else {
        this.$refs.galleryInput.click();
      }
    },
    openCamera () {
      if (!this.isMessagingDisabled) {
        this.$refs.cameraInput.click();
      }
    },
    closeGalleryPopup () {
      this.showGalleryPopup = false;
    },
    ...mapActions('account/pro/pricing', ['fetchPricingList']),
    async uploadPhotos (event) {
      this.showGalleryPopup = false; // Close the popup immediately
      this.uploadPhotoError = '';
      this.$refs.uploadPhotoErrorMessage.hide();

      const photoPromises = uploadFiles(event, { id: this.serviceProviderOccupationId, mode: 'provider' });

      // For each successful photo upload push the photo into the photos array
      if (photoPromises.length <= 8) {
        this.uploadInProgress = true;
        photoPromises.forEach((filePromise) => {
          filePromise.then(({ data }) => {
            this.photos.push(data);
            return data;
          }).catch((error) => {
            if (error && error.message) {
              this.uploadPhotoError = error.message;
            }
            this.$refs.uploadPhotoErrorMessage.trigger(4000);
            return error;
          });
        });


        try {
          await Promise.all(photoPromises);
        } catch (error) {
        // do nothing - errors are handled by individual items
        }

        this.uploadInProgress = false;
      } else {
        this.isShowImagePopup = true;
      }
    },
    async deletePhoto (photoId) {
      await deleteFile({ id: photoId, mode: 'provider' });
      this.photos.splice(this.photos.findIndex(photo => photo.id === photoId), 1);
    },
    async getPhotos () {
      const { data } = await getOccupationsAssets({
        service_provider_occupation_ids: this.serviceProviderOccupationId,
      });
      this.photos = data.results.map((asset) => {
        return { ...asset, url: asset.asset_path };
      });
    },
    async getServices () {
      const { data: { results } } = await getPublicProfiles({
        ids: this.serviceProviderOccupationId,
      });
      this.services = results[0] ? results[0].services : [];
      this.loading = false;
    },
    async getWorkArea () {
      const response = await getOccupationsCoverAreas({
        service_provider_occupation_ids: this.serviceProviderOccupationId,
      });
      // eslint-disable-next-line camelcase
      const ids = response.data.results.map(({ cover_area_id }) => cover_area_id).join(',');
      const { data } = await getCoverAreasDetails({ cover_area_ids: ids, size: RESULTS_SIZE });
      const zipcodes = _uniq(data.results.map(z => z.zip_code));

      try {
        const { data: geocodeData } = await geocode(zipcodes.join(','), 'US', zipcodes.length);

        this.locations = geocodeData.results.map((location) => {
          return {
            latlng: {
              lat: location.geo_point.center_latitude,
              lng: location.geo_point.center_longitude,
            },
          };
        });
      } catch (err) {
        // do nothing
      }
    },
    async getLicenseDocuments () {
      // TODO - call https://coreserviceproviderapi-pr-revamp.techshed-dev.com/apidocs/#/document/getDocument
      // and compare (getOccupationsDocuments.document_meta_name to getDocument.display_name)
      // to determine which documents are actually licenses
      // Do this once core updates the (getDocument) api to accept name as a request parameter
      const { data } = await getOccupationsDocuments({
        service_provider_occupation_ids: this.serviceProviderOccupationId,
      });
      this.licenses = data.results;
    },
    submitRedirect ({ source, occupId, editType }) {
      this.$events.$emit(this.$events.OCCUPATION_EDIT, { type: editType, occupId });
      this.source = source;
      this.occupId = occupId;
      this.$refs.form.submit();
    },
    setServiceProviderOccupationId () {
      if (!this.serviceProviderOccupationId) {
        const [firstOccupation] = Object.keys(this.serviceProviderCatalogOccupations);

        if (!firstOccupation) {
          return;
        }

        this.$router.replace({
          name: 'account::pro::occupation',
          params: {
            serviceProviderOccupationId: this.serviceProviderCatalogOccupations[firstOccupation].id,
          },
        });
      }
    },
    toggleEditPricing () {
      if (this.openPricing) {
        this.services = [];
        this.loading = true;
        this.getServices();
      }
      this.openPricing = !this.openPricing;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools.scss";

  .HEADING {
    @include margin(0 3 3 0);
    @include h5();
  }
  .HEADING-LAYOUT {
    @include h4();
    @include margin(3 0 3 0);
  }

  .DIVIDER {
    @include margin-top(6);
    @include margin-bottom(5);
    border-bottom: 1px solid $color-gray50;
  }

  .PHOTO-UPLOAD-ERROR {
    @include margin-top(3);
    width: 100%;

    @include tablet {
      width: 50%;
    }
  }
  .gallery-popup-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.gallery-popup {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  min-width: 240px;
  text-align: center;
}
  .popup-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
  .BUTTON {
    border: 1px solid $color-depot;
    line-height: 22px;
    color: #fff;
    background-color: $color-depot;
    border-radius: 3px;
    height: 28px;
    .TEXT {
      font-size: 14px;
    }
  }
    .BUTTONCLOSE {
    border: 1px solid $color-depot;
    line-height: 22px;
    color: $color-depot;
    background-color: white;
    border-radius: 3px;
    height: 28px;
    .TEXT {
      font-size: 14px;
    }
  }
  .UNDER:hover {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
</style>
